import * as React from "react";

import { EmptyChat } from "@/components/illustrations/EmptyChat";
import { GradientText } from "@/components/ui/gradient";
import { useChatStore } from "@/stores/chat";
import type { Uuid } from "@/types";

import { MessageBubble } from "./MessageBubble";
import { OnboardingBanner } from "./OnboardingBanner";
import { SuggestedPrompts } from "./SuggestedPrompts";
import { ThoughtIndicator } from "./ThoughtIndicator";

interface ChatStreamProps {
  onWidgetSubmit?: (widget_id: Uuid, data: Record<string, unknown>) => void;
  onWidgetApproval?: (
    widget_id: Uuid,
    action: "save" | "cancel" | "approve" | "reject" | "request_change",
    reason?: string,
  ) => void;
}

export function ChatStream({
  onWidgetSubmit,
  onWidgetApproval,
}: ChatStreamProps = {}): JSX.Element {
  const messages = useChatStore((s) => s.messages);
  const thoughts = useChatStore((s) => s.thoughts);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, thoughts.length, messages]);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-6 min-h-0"
      role="log"
      aria-live="polite"
      aria-label="Hội thoại"
    >
      <div className="max-w-3xl mx-auto">
        {/* Doc 49: gợi ý setup khi user mới (no Odoo / no LLM key) */}
        <OnboardingBanner />
      </div>
      <div className="max-w-3xl mx-auto space-y-6">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onWidgetSubmit={onWidgetSubmit}
              onWidgetApproval={onWidgetApproval}
            />
          ))
        )}
        {thoughts.length > 0 ? <ThoughtIndicator thoughts={thoughts} /> : null}
      </div>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center pt-8 md:pt-12 space-y-4 animate-fade-in">
      <div className="flex justify-center">
        <EmptyChat size={140} />
      </div>
      <GradientText as="h1" className="text-2xl md:text-3xl tracking-tight">
        Em có thể giúp gì cho anh?
      </GradientText>
      <p className="text-muted-foreground text-sm max-w-md mx-auto">
        Cứ nói tự nhiên. Em sẽ tra cứu Odoo, sinh form khi cần thông tin, và xác
        nhận lại trước khi ghi dữ liệu vào hệ thống.
      </p>
      <SuggestedPrompts />
    </div>
  );
}
